import { useEffect, useState } from "react";
import { useSession } from "@clerk/clerk-react";
import { useNavigate } from "react-router-dom";
import { Chip } from "@heroui/react";
import { LifebuoyIcon } from "@phosphor-icons/react";
import {
  AlertQuestion,
  ArrowUpRight,
  Building,
  Star,
  StarFilled,
  Calendar,
  Stack,
  User,
  Tag,
} from "assets/icons";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { TicketSource, HelpdeskTicketInterface, TicketLabel } from "shared/types/helpdesk";
import { UserSelection } from "shared/components/userSelection/userSelection";
import { ClerkUser } from "shared/types/user";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { DashboardBlock, DashboardBlockHeader, DashboardBlockContent } from "../layout/DashboardBlock";
import { BlockEmptyState } from "../states/BlockStates";
import { blockStyles } from "../utils/blockStyles.utils";

interface HelpdeskTicketProps {
  ticket: HelpdeskTicketInterface;
  onClick: () => void;
  onAssigneeChange: (ticket: any, newAssignee: ClerkUser | null) => void;
  isUpdating: boolean;
}

/**
 * Helpdesk Tickets Block
 * Displays new helpdesk tickets from the last 7 days
 */
export const HelpdeskTicketsBlockNew = () => {
  const { session } = useSession();
  const navigate = useNavigate();
  const [ticketsData, setTicketsData] = useState({
    newTickets: [] as any[],
  });
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    const fetchTickets = async () => {
      try {
        const now = new Date();
        const dayOfWeek = now.getDay();
        // Monday-based week start (getDay: 0=Sun, 1=Mon, ..., 6=Sat)
        const daysSinceMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        const startOfWeek = new Date(now.getFullYear(), now.getMonth(), now.getDate() - daysSinceMonday);
        startOfWeek.setHours(0, 0, 0, 0);

        const params = new URLSearchParams({
          startDate: startOfWeek.toISOString(),
          sortField: 'createdAt',
          sortDirection: 'DESC',
          pageSize: '20',
        });
        const response = await fetchWithAuth(
          `${API_URL}/helpdesk/tickets?${params.toString()}`,
          { method: "GET" },
          session,
        );
        if (!response.ok) {
          const errorText = await response.text().catch(() => 'Unknown error');
          throw new Error(`Failed to fetch tickets: ${response.status} - ${errorText}`);
        }
        const result = await response.json();
        const newTickets = result.data || [];
        
        setTicketsData({ newTickets });
      } catch (error) {
        console.error("Error fetching helpdesk tickets:", error);
        Sentry.captureException(error, { tags: { component: 'HelpdeskTicketsBlockNew' } });
      }
    };

    if (session) {
      fetchTickets();
    }
  }, [session]);

  const handleAssigneeChange = async (ticket: any, newAssignee: ClerkUser | null) => {
    if (ticket.assignedTo === newAssignee?.id) return;
    
    try {
      setIsUpdating(true);
      const response = await fetchWithAuth(
        `${API_URL}/helpdesk/ticket/${ticket.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ assignedTo: newAssignee?.id || null }),
        },
        session
      );
      
      if (response.ok) {
        setTicketsData(prevData => ({
          ...prevData,
          newTickets: prevData.newTickets.map(t => 
            t.id === ticket.id ? { ...t, assignedTo: newAssignee?.id || null } : t
          )
        }));
        toast.success(`Ticket toegewezen aan ${newAssignee ? `${newAssignee.first_name} ${newAssignee.last_name}` : 'niemand'}`);
      } else {
        throw new Error("Failed to update assignee");
      }
    } catch (error) {
      console.error("Error updating assignee:", error);
      toast.error("Error bij updaten toegewezen persoon");
    } finally {
      setIsUpdating(false);
    }
  };

  const HelpdeskTicket: React.FC<HelpdeskTicketProps> = ({
    ticket,
    onClick,
    onAssigneeChange,
    isUpdating,
  }) => {
    const renderPriorityStars = (priority: any) => {
      const priorityLevel = priority === 'Hoog' ? 3 : priority === 'Gemiddeld' ? 2 : priority === 'Laag' ? 1 : 0;
      const stars = [];
      
      for (let i = 0; i < priorityLevel; i++) {
        stars.push(<StarFilled key={`filled_${i}`} className="h-3 w-3 text-brick-primary" />);
      }
      for (let i = priorityLevel; i < 3; i++) {
        stars.push(<Star key={`empty_${i}`} className="h-3 w-3 text-neutral-secondary-mid" />);
      }
      return stars;
    };

    const stripHtml = (html: any) => {
      if (!html) return "";
      const temporalDivElement = document.createElement("div");
      temporalDivElement.innerHTML = html;
      return temporalDivElement.textContent || temporalDivElement.innerText || "";
    };

    const getDaysOld = (createdAt: string | Date) => {
      const created = typeof createdAt === 'string' ? new Date(createdAt) : createdAt;
      const now = new Date();
      const diffTime = Math.abs(now.getTime() - created.getTime());
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    };

    const daysOld = getDaysOld(ticket.createdAt || new Date());
    const isOverdue = daysOld > 7;

    return (
      <div
        className={`flex w-full cursor-pointer flex-col gap-2.5 overflow-hidden p-3 transition-colors border-b border-neutral-primary/5 last:border-b-0 ${isOverdue ? '' : ''}`}
        onClick={onClick}
      >
        {/* Header with ID, title, priority and assignee */}
        <header className="flex w-full items-start justify-between gap-2">
          <div className="flex flex-col gap-1 grow min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-bold text-brick-primary text-sm">#H{ticket.id}</span>
              <div className="flex items-center gap-1">
                {renderPriorityStars(ticket.priority || 0)}
              </div>
              {isOverdue && (
                <span className="text-xs text-red-600 bg-red-100 px-1.5 py-0.5 rounded-sm border border-red-200">
                  {daysOld}d oud
                </span>
              )}
            </div>
            <p className="font-medium text-neutral-primary text-sm line-clamp-2 leading-tight">
              {ticket.title || 'Geen titel'}
            </p>
          </div>
          <div className="flex items-center shrink-0">
            <div onClick={(e) => e.stopPropagation()}>
              <UserSelection
                defaultUser={ticket.assignedTo}
                onUserSelect={(newAssignee: ClerkUser | null) => onAssigneeChange(ticket, newAssignee)}
                isDisabled={isUpdating}
              />
            </div>
          </div>
        </header>

        {/* Key Information Row */}
        <div className="flex flex-wrap gap-1.5">
          {/* Source */}
          {ticket.source && ticket.source !== TicketSource.UNKNOWN && (
            <Chip
              size="sm"
              classNames={{
                base: `h-fit px-1.5 py-0.5 border text-xs font-medium ${
                  ticket.source === TicketSource.TENANT ? 'bg-moss-primary/10 border-moss-primary text-moss-primary' :
                  ticket.source === TicketSource.INTERNAL ? 'bg-brick-primary/10 border-brick-primary text-brick-primary' :
                  'bg-yellow-500/10 border-yellow-500 text-yellow-600'
                }`,
                content: "px-0 font-medium",
              }}
            >
              {ticket.source === TicketSource.TENANT ? '🏠' :
               ticket.source === TicketSource.INTERNAL ? '🏢' : '👥'}
              <span className="ml-1">
                {ticket.source === TicketSource.TENANT ? 'Huurder' :
                 ticket.source === TicketSource.INTERNAL ? 'Intern' : 'Buur'}
              </span>
            </Chip>
          )}

          {/* Building */}
          {ticket.building && (
            <Chip
              size="sm"
              startContent={<Building className="h-3 w-3 text-moss-primary" />}
              classNames={{
                base: "h-fit bg-moss-primary/10 border border-moss-primary text-moss-primary text-xs px-1.5 py-0.5",
                content: "px-0 font-medium",
              }}
            >
              {ticket.building.id}
            </Chip>
          )}

          {/* Unit */}
          {ticket.unit && (
            <Chip
              size="sm"
              startContent={<Stack className="h-3 w-3 text-moss-primary" />}
              classNames={{
                base: "h-fit bg-moss-primary/10 border border-moss-primary text-moss-primary text-xs px-1.5 py-0.5",
                content: "px-0 font-medium",
              }}
            >
              {ticket.unit.id}
            </Chip>
          )}

          {/* Reporter */}
          {ticket.reporter && (
            <Chip
              size="sm"
              startContent={<User className="h-3 w-3 text-brick-primary" />}
              classNames={{
                base: "h-fit bg-brick-secondary-fine border border-brick-primary text-brick-primary text-xs px-1.5 py-0.5",
                content: "px-0 font-medium",
              }}
            >
              <span className="truncate max-w-16">
                {(ticket.reporter as any).firstName || ''} {(ticket.reporter as any).lastName || ''}
              </span>
            </Chip>
          )}

          {/* Label */}
          {ticket.label && ticket.label !== TicketLabel.UNKNOWN && (
            <Chip
              size="sm"
              startContent={<Tag className="h-3 w-3 text-brick-primary" />}
              classNames={{
                base: "h-fit bg-brick-secondary-fine border border-brick-primary text-brick-primary text-xs px-1.5 py-0.5",
                content: "px-0 font-medium",
              }}
            >
              <span className="truncate max-w-20">{ticket.label}</span>
            </Chip>
          )}
        </div>

        {/* Description */}
        <div className="flex flex-col">
          <p className="line-clamp-2 w-full text-xs text-neutral-primary leading-relaxed">
            {stripHtml(ticket.description) || "Geen beschrijving beschikbaar"}
          </p>
        </div>

        {/* Date info */}
        <div className="flex items-center gap-2 text-xs text-neutral-secondary-mid">
          <Calendar className="h-3 w-3" />
          <span>{new Date(ticket.createdAt || '').toLocaleDateString('nl-NL')}</span>
        </div>
      </div>
    );
  };

  return (
    <DashboardBlock>
      <DashboardBlockHeader
        icon={<LifebuoyIcon weight="duotone" size={20} />}
        title="Nieuwe helpdesktickets"
        action={
          <button
            onClick={() => navigate("/helpdesk")}
            className={`${blockStyles.link.variants.primary} ${blockStyles.link.withIcon}`}
          >
            Bekijk alle <ArrowUpRight className="h-[18px] w-[18px]" />
          </button>
        }
      />
      <DashboardBlockContent padding="none" className="relative overflow-hidden">
        {ticketsData.newTickets.length > 0 ? (
          <>
            <div className="relative z-1 flex flex-col">
              {ticketsData.newTickets
                .slice() 
                .sort((a: any, b: any) => {
                  const dateA = new Date(a.createdAt || '').getTime();
                  const dateB = new Date(b.createdAt || '').getTime();
                  return dateB - dateA; // Most recent first
                })
                .slice(0, 6) // Show max 6 tickets
                .map((ticket, index) => (
                  <HelpdeskTicket
                    key={`${ticket.id}-${index}`}
                    ticket={ticket}
                    onClick={() => navigate(`/helpdesk/${ticket.id}`)}
                    onAssigneeChange={handleAssigneeChange}
                    isUpdating={isUpdating}
                  />
                ))}
            </div>
            <div className="absolute bottom-0 right-0 z-0 translate-x-1/4 translate-y-1/4 text-brick-primary/5 pointer-events-none">
              <AlertQuestion className="h-[120px] w-[120px]" />
            </div>
          </>
        ) : (
          <BlockEmptyState
            icon={<LifebuoyIcon weight="duotone" size={48} />}
            title="Geen nieuwe tickets"
            description="Alle tickets zijn behandeld"
          />
        )}
      </DashboardBlockContent>
    </DashboardBlock>
  );
};
